import React, { useState } from "react";
import axios from "axios";
import Header from "./Header.tsx";
import Sidebar from "./Sidebar.tsx";
import styles from "./PumpDashboard.module.css";
import useTheme from "../hooks/useTheme.ts";

const AssetView: React.FC = () => {
  const { theme } = useTheme();

  const [assetCode, setAssetCode] = useState("");
  const [assetData, setAssetData] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

  const apiUrl = backendUrl.endsWith("/api") ? backendUrl : `${backendUrl}/api`;

  const searchAssetStatus = async () => {
    const trimmedAssetCode = assetCode.trim();

    if (!trimmedAssetCode) {
      setMessage("Please enter asset/pump name.");
      setAssetData(null);
      return;
    }

    try {
      setLoading(true);
      setMessage("");
      setAssetData(null);

      const token =
        localStorage.getItem("jwt") || localStorage.getItem("token");

      const response = await axios.get(
        `${apiUrl}/asset-status/${encodeURIComponent(trimmedAssetCode)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data?.data;

      if (!data) {
        setMessage("No asset status found.");
        return;
      }

      setAssetData(data);
    } catch (error) {
      console.error("Error fetching asset status:", error);
      setMessage("Failed to fetch asset status.");
      setAssetData(null);
    } finally {
      setLoading(false);
    }
  };

  const clearAssetSearch = () => {
    setAssetCode("");
    setAssetData(null);
    setMessage("");
  };

  return (
    <section
      className={`${styles.section} ${
        theme === "dark" ? "dark-theme" : "light-theme"
      }`}
    >
      <Header />

      <div className={styles.container}>
        <div className={styles.sidebarStyle}>
          <Sidebar />
        </div>

        <main className={styles.mainContentStyle}>
          <div className={styles.pageHeader}>
            <h1 className={styles.title}>Asset View</h1>
            <p className={styles.cardSubtitle}>
              Search asset status by pump/unit name.
            </p>
          </div>

          <section className={styles.tableCard}>
            <div
              className={`${styles.tableHeaderRow} ${styles.assetSearchHeaderRow}`}
            >
              <div className={styles.assetSearchTitleBlock}>
                <h2 className={styles.cardTitle}>Asset Search</h2>
                <p className={styles.cardSubtitle}>
                  Enter pump / asset code only.
                </p>
              </div>

              <div className={styles.assetSearchToolbar}>
                <div
                  className={`${styles.searchInputWrapper} ${styles.assetSearchInputWrapper}`}
                >
                  <input
                    className={`${styles.searchInput} ${
                      assetCode ? styles.searchInputWithClear : ""
                    }`}
                    value={assetCode}
                    onChange={(e) => setAssetCode(e.target.value)}
                    placeholder="Example: FPF105"
                    aria-label="Search asset or pump"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !loading) {
                        searchAssetStatus();
                      }
                    }}
                  />

                  {assetCode && (
                    <button
                      type="button"
                      className={styles.searchClearButton}
                      onClick={clearAssetSearch}
                      aria-label="Clear asset search"
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  className={styles.assetSearchButton}
                  onClick={searchAssetStatus}
                  disabled={loading}
                >
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>

            {message && <p className={styles.assetStatusMessage}>{message}</p>}

            {assetData && (
              <div className={styles.assetStatusTableWrap}>
                <table className={styles.assetStatusTable}>
                  <tbody>
                    {Object.entries(assetData)
                      .filter(
                        ([, value]) =>
                          value !== null && value !== undefined && value !== "",
                      )
                      .map(([key, value]) => (
                        <tr key={key}>
                          <th>{key}</th>
                          <td>{String(value)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    </section>
  );
};

export default AssetView;
